import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, getToken } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const Field = ({ field, value, onChange }) => {
  const id = `field-${field.key}`;
  const common = { id, "data-testid": `intake-field-${field.key}`,
                   "aria-required": field.required || undefined };
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="text-sm font-medium">
        {field.label}{field.required && <span aria-hidden="true"> *</span>}
        {field.secure && <span className="text-xs text-muted-foreground"> (restricted)</span>}
      </label>
      {field.type === "textarea" ? (
        <textarea {...common} className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                  rows={3} value={value ?? ""}
                  onChange={(e) => onChange(field, e.target.value)} />
      ) : field.type === "select" ? (
        <select {...common} className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                value={value ?? ""} onChange={(e) => onChange(field, e.target.value)}>
          <option value="">Choose…</option>
          {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : field.type === "boolean" ? (
        <input {...common} type="checkbox" checked={Boolean(value)}
               onChange={(e) => onChange(field, e.target.checked)} />
      ) : (
        <Input {...common} type={field.type === "number" ? "number"
                                 : field.type === "date" ? "date" : "text"}
               value={value ?? ""} onChange={(e) => onChange(field, e.target.value)} />
      )}
      {field.help_text && (
        <p className="text-xs text-muted-foreground">{field.help_text}</p>
      )}
    </div>
  );
};

export default function Catalog() {
  const [catalog, setCatalog] = useState({ services: [] });
  const [intake, setIntake] = useState(null);
  const [values, setValues] = useState({});
  const [location, setLocation] = useState("");
  const [title, setTitle] = useState("");
  const [search, setSearch] = useState("");
  const [submitted, setSubmitted] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!getToken()) { navigate("/login"); return; }
    apiFetch("/catalog").then((r) => (r.ok ? r.json() : { services: [] })).then(setCatalog);
  }, [navigate]);

  // Phase 18: the portal home sends people here with ?item=<service key> when their words
  // clearly matched one service, so the form opens instead of another list to read.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const item = params.get("item");
    const q = params.get("q");
    if (q) setSearch(q);
    if (!item) return;
    apiFetch(`/requests/intake/${item}`).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError("That service is no longer open to you — choose from the catalogue below.");
        return;
      }
      setIntake(data);
      setLocation(data.default_location_key || "");
    });
  }, []);

  const openIntake = async (key) => {
    setError(null); setSubmitted(null); setValues({}); setTitle("");
    const res = await apiFetch(`/requests/intake/${key}`);
    const data = await res.json();
    if (!res.ok) { setError(JSON.stringify(data.detail)); return; }
    setIntake(data);
    setLocation(data.default_location_key || "");
  };

  const change = (field, raw) => {
    const value = field.type === "number" ? (raw === "" ? "" : Number(raw)) : raw;
    setValues((v) => ({ ...v, [field.key]: value }));
  };

  const submit = async () => {
    setError(null);
    const fields = Object.fromEntries(
      Object.entries(values).filter(([, v]) => v !== "" && v !== null && v !== undefined));
    const res = await apiFetch("/requests", {
      method: "POST",
      body: JSON.stringify({ service_key: intake.service_key, fields,
                             title: title || undefined, channel: "portal",
                             location_key: location || undefined }),
    });
    const data = await res.json();
    if (!res.ok) { setError(typeof data.detail === "string" ? data.detail
                                                            : JSON.stringify(data.detail)); return; }
    setSubmitted(data);
    setIntake(null);
  };

  const items = catalog.services.flatMap((s) => [s, ...(s.offerings || [])]);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" data-testid="catalog-title">Service Catalog</h1>
          <p className="text-sm text-muted-foreground">
            Raise work here and it lands in the right queue for the right site.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm" data-testid="catalog-queue-link">
            <Link to="/queue">Request queue</Link>
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 space-y-8">
        {error && <p className="text-sm text-destructive" data-testid="catalog-error">{error}</p>}

        {submitted && (
          <section className="border rounded-md p-4 space-y-2" data-testid="intake-receipt">
            <h2 className="text-base md:text-lg font-medium">
              Raised <span className="font-mono">{submitted.number}</span>
            </h2>
            <p className="text-sm" data-testid="intake-receipt-routing">
              Queue: {submitted.queue || "unrouted"} · site {submitted.location_key || "none"} ·
              priority {submitted.priority}
            </p>
            <p className="text-sm text-muted-foreground" data-testid="intake-receipt-reason">
              {submitted.routing_reason}
            </p>
            {(submitted.known_error_suggestions || []).length > 0 && (
              <div data-testid="intake-known-errors">
                <h3 className="text-sm font-semibold mt-2">Known errors that may help now</h3>
                <ul className="text-sm list-disc pl-5">
                  {submitted.known_error_suggestions.map((k) => (
                    <li key={k.id || k.number}>{k.title}{k.workaround ? ` — ${k.workaround}` : ""}</li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        {!intake && (
          <section className="space-y-3" data-testid="catalog-list">
            <div className="border rounded-md p-4 space-y-1" data-testid="catalog-knowledge-first">
              <h2 className="text-base md:text-lg font-medium">Try knowledge first</h2>
              <p className="text-sm text-muted-foreground">
                Published guidance and known errors often solve it in a minute — no waiting.
              </p>
              <Button asChild size="sm" variant="outline" data-testid="catalog-knowledge-link">
                <Link to="/knowledge">Search knowledge</Link>
              </Button>
            </div>
            <h2 className="text-base md:text-lg font-medium">What do you need?</h2>
            <div className="space-y-1">
              <label htmlFor="catalog-search" className="text-sm font-medium">
                Search the catalog
              </label>
              <Input id="catalog-search" data-testid="catalog-search" value={search}
                     placeholder="Laptop, access, facilities…"
                     onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {items
                .filter((item) => `${item.name} ${item.description}`.toLowerCase()
                  .includes(search.toLowerCase()))
                .slice(0, 24)
                .map((item) => (
                <div key={item.key} className="border rounded-md p-4 space-y-2"
                     data-testid={`catalog-item-${item.key}`}>
                  <p className="font-medium text-sm">{item.name}</p>
                  <p className="text-xs text-muted-foreground">{item.description}</p>
                  <Button size="sm" variant="outline" data-testid={`catalog-raise-${item.key}`}
                          onClick={() => openIntake(item.key)}>
                    Raise this
                  </Button>
                </div>
              ))}
              {items.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="catalog-empty">
                  Nothing is published in the catalog yet.
                </p>
              )}
            </div>
          </section>
        )}

        {intake && (
          <section className="space-y-4" data-testid="intake-form">
            <div className="flex items-center justify-between">
              <h2 className="text-base md:text-lg font-medium" data-testid="intake-form-title">
                {intake.name}
              </h2>
              <Button size="sm" variant="ghost" data-testid="intake-cancel"
                      onClick={() => setIntake(null)}>Back to catalog</Button>
            </div>
            <p className="text-sm text-muted-foreground" data-testid="intake-routing-preview">
              {intake.provider_name || intake.provider_key} · expected queue{" "}
              {intake.expected_queue} — {intake.routing_reason}
            </p>

            <div className="space-y-1">
              <label htmlFor="intake-title" className="text-sm font-medium">
                Short summary
              </label>
              <Input id="intake-title" data-testid="intake-title" value={title}
                     onChange={(e) => setTitle(e.target.value)} />
            </div>

            <div className="space-y-1">
              <label htmlFor="intake-location" className="text-sm font-medium">
                Where is this needed?
              </label>
              <select id="intake-location" data-testid="intake-location" value={location}
                      className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                      onChange={(e) => setLocation(e.target.value)}>
                {intake.locations.map((l) => (
                  <option key={l.key} value={l.key}>{l.name}</option>
                ))}
              </select>
            </div>

            {intake.fields.map((f) => (
              <Field key={f.key} field={f} value={values[f.key]} onChange={change} />
            ))}
            {intake.fields.length === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="intake-no-fields">
                This item has no extra questions.
              </p>
            )}

            <Button data-testid="intake-submit" onClick={submit}>Submit request</Button>
          </section>
        )}
      </main>
    </div>
  );
}
